import React, { useEffect, useState } from 'react';
import {
    Modal,
    TextInput,
    Stack,
    Button,
    Group,
    Select,
    NumberInput,
    Divider,
    Paper,
    Text,
    Switch,
    Stepper,
    Box,
    Badge,
    ThemeIcon,
    useMantineTheme,
    LoadingOverlay,
    Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { env } from '../../utils/env';
import CheckIcon from '../../assets/icons/check.svg';
import ClockIcon from '../../assets/icons/clock.svg';
import CalendarIcon from '../../assets/icons/calendar.svg';
import CategoryIcon from '../../assets/icons/category.svg';
import DollarIcon from '../../assets/icons/dollar.svg';

import { useRules, Rule, RuleCreateParams, RuleUpdateParams } from '../../hooks/useRules';
import type { RuleCondition } from '../../hooks/useRules';
import { Transaction } from '../../types/transaction';
import { useBudgetCategories } from '../../hooks/useBudgetCategories';
import { useGroups } from '../../hooks/useGroups';
import { useGroupCategories } from '../../hooks/useGroupCategories';
import { CategorySelector } from '../common/CategorySelector';
import { RuleSplitControls } from '../rules/RuleSplitControls';
import { RuleConditionsControls } from '../rules/RuleConditionsControls';

// The placeholder hook can be removed when we have the real implementation
// Remove the placeholders once we have accounts passed as props
// interface FinancialAccount {
//     id: string; // Or number, adjust as needed
//     name: string;
// }

// const useFinancialAccounts = (accessToken: string): { accounts: FinancialAccount[], loading: boolean, error: string | null } => {
//     // This is a placeholder. Implement actual fetching logic.
//     console.warn('Placeholder useFinancialAccounts hook used. Implement actual data fetching.');
//     const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState<string | null>(null);

//     useEffect(() => {
//         if (!accessToken) {
//             setLoading(false);
//             return;
//         }
//         // Simulate API call
//         setTimeout(() => {
//             setAccounts([
//                 { id: 'acc_1', name: 'Checking Account' },
//                 { id: 'acc_2', name: 'Savings Account' },
//                 { id: 'acc_3', name: 'Credit Card Visa' },
//             ]);
//             setLoading(false);
//         }, 1000);
//     }, [accessToken]);

//     return { accounts, loading, error };
// };

// Rule types and their UI representations
const RULE_TYPE_OPTIONS = [
    { value: 'transaction', label: 'Transaction Rule' },
    { value: 'group_expense', label: 'Group Expense Rule' },
];

interface RuleModalProps {
    opened: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    initialRule?: Rule | null;
    transaction?: Transaction | null;
    groupId?: number;
    accessToken: string;
    accounts?: { id: string; name: string; institution_name?: string }[];
}

export const RuleModal: React.FC<RuleModalProps> = ({
    opened,
    onClose,
    onSuccess,
    initialRule,
    transaction,
    groupId,
    accessToken,
    accounts = [],
}) => {
    const theme = useMantineTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [ruleType, setRuleType] = useState<'transaction' | 'group_expense' | 'shared'>(
        initialRule?.rule_type || 'transaction'
    );
    const [submitting, setSubmitting] = useState(false);

    // Fetch budget and group data
    const {
        categories: budgetCategories,
        headers: budgetHeaders,
        loading: loadingBudgetCategories,
        error,
    } = useBudgetCategories(accessToken);
    const { groups: userGroups, loading: loadingUserGroups, refresh: refreshUserGroups } = useGroups(accessToken, { isVisible: false });

    // Group members state for split controls
    const [groupMembers, setGroupMembers] = useState<any[]>([]);
    const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);

    // Financial accounts loading state
    const [loadingFinancialAccounts, setLoadingFinancialAccounts] = useState(false);

    // Use the rules hook to manage rules
    const {
        createRule,
        updateRule,
        deleteRule,
        toggleRuleStatus,
        getOperatorsForField,
        applyRuleOnce,
        applyGroupExpenseRuleOnce,
    } = useRules(accessToken, undefined, groupId);

    // Create form with validation
    const form = useForm<RuleCreateParams>({
        initialValues: {
            name: '',
            rule_type: ruleType,
            conditions: [
                {
                    field: 'merchant_name',
                    operator: 'contains',
                    value: '',
                }
            ],
            condition_operator: 'and',
            enabled: true,
            priority: 0,
            target_budget_category_id: undefined,
            target_group_category_id: undefined,
            group_id: groupId,
            split_type: 'equal',
            split_with: {},
            apply_to_past_transactions: false,
        },
        validate: {
            name: (value) => (!value.trim() ? 'Name is required' : null),
            conditions: (value) => {
                if (!value || value.length === 0) return 'At least one condition is required';

                // Check each condition
                for (const condition of value) {
                    if (!condition.field) return 'Field is required';
                    if (!condition.operator) return 'Operator is required';
                    if (!condition.value.toString().trim()) return 'Value is required';
                }

                return null;
            },
            group_id: (value, values) => {
                if (values.rule_type === 'group_expense' && !value) {
                    return 'Group is required for group expense rules';
                }
                return null;
            },
        },
    });

    // Now that form is defined, we can use form.values.group_id
    const { categories: groupCategories, loading: loadingGroupCategories } = useGroupCategories(accessToken, form.values.group_id);

    // Fetch group members when needed for split controls
    useEffect(() => {
        const fetchGroupMembers = async (id: number) => {
            if (!accessToken || !id) return;

            try {
                setLoadingGroupMembers(true);
                const response = await fetch(`${env.API_URL}/api/groups/${id}/members`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch group members: ${response.status}`);
                }

                const data = await response.json();
                setGroupMembers(data);
            } catch (err) {
                console.error('Error fetching group members:', err);
            } finally {
                setLoadingGroupMembers(false);
            }
        };

        // Fetch members when the group ID changes, but only if in group_expense mode
        // and only if the group ID is valid and present
        const currentGroupId = form.values.group_id;
        if (currentGroupId && ruleType === 'group_expense' && opened) {
            fetchGroupMembers(currentGroupId);
        }
    }, [form.values.group_id, ruleType, accessToken, opened]);

    // Update group fetch logic to prevent excessive fetching
    useEffect(() => {
        const shouldRefresh = opened && ruleType === 'group_expense' && !userGroups.length;
        if (shouldRefresh) {
            refreshUserGroups();
        }
    }, [opened, ruleType, refreshUserGroups, userGroups.length, loadingUserGroups]);

    // Update form when initialRule or transaction changes
    useEffect(() => {
        if (initialRule) {
            // Edit mode - populate form with rule data
            setRuleType(initialRule.rule_type);

            // Set form values
            form.setValues({
                name: initialRule.name,
                rule_type: initialRule.rule_type,
                conditions: initialRule.conditions || [
                    {
                        field: 'merchant_name' as const,
                        operator: 'contains' as const,
                        value: '',
                    }
                ],
                condition_operator: initialRule.condition_operator || 'and',
                enabled: initialRule.enabled,
                priority: initialRule.priority || 0,
                target_budget_category_id: initialRule.target_budget_category_id,
                target_group_category_id: initialRule.target_group_category_id,
                group_id: initialRule.group_id || groupId,
                split_type: initialRule.split_type || 'equal',
                split_with: initialRule.split_with || {},
                paid_by_id: initialRule.paid_by_id,
                start_date: initialRule.start_date,
                end_date: initialRule.end_date,
            });
        } else if (transaction && opened) {
            // Pre-fill form based on transaction
            const prefillRuleType = groupId ? 'group_expense' : 'transaction';
            setRuleType(prefillRuleType); // Set ruleType state early

            const prefillValues: Partial<RuleCreateParams> = {
                name: `Rule for ${transaction.merchant_name || transaction.description || 'Transaction'}`,
                rule_type: prefillRuleType, // Use the determined type
                enabled: true,
                group_id: groupId,
                split_type: 'equal',
                split_with: {},
            };

            // Set target category if available
            if (transaction.category_id) {
                if (prefillRuleType === 'transaction') {
                    prefillValues.target_budget_category_id = Number(transaction.category_id);
                } else {
                    prefillValues.target_group_category_id = Number(transaction.category_id);
                }
            }

            // First, set the basic prefill values (name, rule_type, category targets, etc.)
            form.setValues(prefillValues);

            // Create a single default condition
            let defaultCondition: RuleCondition;
            if (transaction.merchant_name) {
                defaultCondition = {
                    field: 'merchant_name',
                    operator: 'contains',
                    value: transaction.merchant_name,
                };
            } else if (transaction.description) {
                defaultCondition = {
                    field: 'description',
                    operator: 'contains',
                    value: transaction.description,
                };
            } else {
                defaultCondition = {
                    field: 'merchant_name',
                    operator: 'contains',
                    value: '',
                };
            }

            // Then apply the single prepared condition
            form.setFieldValue('conditions', [defaultCondition]);

            // No need to call setRuleType again here as it's done at the start of this block
        } else if (!initialRule && !transaction && opened) {
            // New rule, and modal has just opened
            const defaultNewRuleType = groupId ? 'group_expense' : 'transaction';
            setRuleType(defaultNewRuleType); // Set state for new rule

            form.reset(); // Reset to useForm's initialValues.
            // Ensure rule_type in form matches current state ruleType, which should be default here
            form.setFieldValue('rule_type', defaultNewRuleType);
            form.setFieldValue('group_id', groupId); // If creating new rule in group context
            form.setFieldValue('enabled', true);
            form.setFieldValue('split_type', 'equal');
            form.setFieldValue('split_with', {});
            form.setFieldValue('conditions', [
                {
                    field: 'merchant_name' as const,
                    operator: 'contains' as const,
                    value: '',
                }
            ]);
            form.setFieldValue('condition_operator', 'and');
        } else if (!opened) {
            // Modal is closed, form.reset() is handled by resetAndClose.
            // Optionally, reset ruleType state to a default for the next opening.
            const nextOpenDefaultRuleType = groupId ? 'group_expense' : 'transaction';
            setRuleType(nextOpenDefaultRuleType);
        }
        console.log('[useEffect init/update] Form values set:', form.values);
    }, [initialRule, transaction, opened, groupId]); // MODIFIED: Removed form from dependencies

    // Handle rule type change
    const handleRuleTypeChange = (value: string | null) => {
        if (value) {
            const newRuleType = value as 'transaction' | 'group_expense' | 'shared';
            setRuleType(newRuleType);
            form.setFieldValue('rule_type', newRuleType);

            // Reset category fields and group when changing rule type
            if (newRuleType === 'transaction') {
                form.setFieldValue('target_group_category_id', undefined);
                form.setFieldValue('group_id', undefined);
                form.setFieldValue('split_type', undefined);
                form.setFieldValue('split_with', {});
            } else {
                form.setFieldValue('target_budget_category_id', undefined);
                // If we already have a groupId from props, use that
                if (groupId) {
                    form.setFieldValue('group_id', groupId);
                }
            }
        }
        console.log('[handleRuleTypeChange] Form values after type change:', form.values);
    };

    // Handle form submission
    const handleSubmit = async (values: RuleCreateParams) => {
        try {
            setSubmitting(true);

            // Format value field based on type - this is now handled by RuleConditionsControls
            let formattedValues = { ...values };

            // Handle conditions formatting if needed
            for (let i = 0; i < formattedValues.conditions.length; i++) {
                const condition = formattedValues.conditions[i];

                if (condition.field === 'amount' && condition.operator === 'between') {
                    const [min, max] = condition.value.split(',').map(Number);
                    formattedValues.conditions[i] = {
                        ...condition,
                        value: `${min.toFixed(2)},${max.toFixed(2)}`
                    };
                } else if (condition.field === 'date') {
                    if (condition.operator === 'in_range') {
                        // This should already be a properly formatted JSON string
                        try {
                            const dates = JSON.parse(condition.value);

                            // Validate dates before using
                            const startDate = dates.start ? new Date(dates.start) : null;
                            const endDate = dates.end ? new Date(dates.end) : null;

                            formattedValues.conditions[i] = {
                                ...condition,
                                value: JSON.stringify({
                                    start: startDate && !isNaN(startDate.getTime()) ? startDate.toISOString() : null,
                                    end: endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : null,
                                })
                            };
                        } catch (e) {
                            console.error('Error formatting date range:', e);
                        }
                    } else {
                        // Single date format
                        try {
                            const dateValue = condition.value ? new Date(condition.value) : null;
                            if (dateValue && !isNaN(dateValue.getTime())) {
                                formattedValues.conditions[i] = {
                                    ...condition,
                                    value: dateValue.toISOString()
                                };
                            }
                        } catch (e) {
                            console.error('Error formatting date:', e);
                        }
                    }
                }
            }

            // Process rule based on whether we're creating, updating, or just applying once
            if (initialRule) {
                // Update
                const result = await updateRule(initialRule.id, formattedValues);
                console.log('Rule updated:', result);
                notifications.show({
                    title: 'Rule Updated',
                    message: 'Your rule has been updated successfully.',
                    color: 'green',
                });
            } else {
                // Create
                const result = await createRule(formattedValues);
                console.log('Rule created:', result);
                notifications.show({
                    title: 'Rule Created',
                    message: 'Your rule has been created successfully.',
                    color: 'green',
                });
            }

            if (onSuccess) {
                onSuccess();
            }

            resetAndClose();
        } catch (err) {
            console.error('Error creating/updating rule:', err);
            notifications.show({
                title: 'Error',
                message: err instanceof Error ? err.message : 'An error occurred.',
                color: 'red',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle rule deletion
    const handleDelete = async () => {
        if (!initialRule) return;

        try {
            setSubmitting(true);
            const success = await deleteRule(initialRule.id);
            if (success) {
                notifications.show({
                    title: 'Rule Deleted',
                    message: 'Your rule has been deleted successfully.',
                    color: 'green',
                });

                if (onSuccess) {
                    onSuccess();
                }

                resetAndClose();
            } else {
                throw new Error('Failed to delete rule');
            }
        } catch (err) {
            console.error('Error deleting rule:', err);
            notifications.show({
                title: 'Error',
                message: err instanceof Error ? err.message : 'Failed to delete rule.',
                color: 'red',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle Apply Once button
    const handleApplyOnce = async () => {
        if (!transaction) return;

        try {
            setSubmitting(true);
            let result;

            if (ruleType === 'transaction') {
                result = await applyRuleOnce(transaction.transaction_id, form.values);
            } else if (ruleType === 'group_expense') {
                result = await applyGroupExpenseRuleOnce(transaction.transaction_id, form.values);
            } else {
                throw new Error('Unsupported rule type for apply once');
            }

            if (result.matched) {
                notifications.show({
                    title: 'Rule Applied',
                    message: result.updated
                        ? 'Rule was matched and applied successfully!'
                        : 'Rule matched but didn\'t require any changes.',
                    color: result.updated ? 'green' : 'blue',
                });

                if (result.updated && onSuccess) {
                    onSuccess();
                }

                resetAndClose();
            } else {
                notifications.show({
                    title: 'Not Matched',
                    message: 'The rule did not match this transaction.',
                    color: 'yellow',
                });
            }
        } catch (err) {
            console.error('Error applying rule once:', err);
            notifications.show({
                title: 'Error',
                message: err instanceof Error ? err.message : 'Failed to apply rule.',
                color: 'red',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Close modal and reset
    const resetAndClose = () => {
        setActiveStep(0);
        form.reset();
        setSubmitting(false);
        onClose();
    };

    // Define steps for the rule creation/editing process
    const steps = [
        { label: 'Rule Details', description: 'Set rule name and conditions' },
        { label: 'Rule Action', description: 'Define what happens when conditions match' },
    ];

    // Render proper category selector based on rule type
    const renderCategorySelector = () => {
        if (ruleType === 'transaction') {
            console.log('RuleModal - Rendering transaction category selector:', {
                categoriesCount: budgetCategories?.length || 0,
                headersCount: budgetHeaders?.length || 0,
                targetCategoryId: form.values.target_budget_category_id,
                loading: loadingBudgetCategories
            });

            // Prepare category data for the dropdown with correct nested structure
            const categoryOptions = budgetHeaders && budgetHeaders.length > 0
                ? budgetHeaders.map(header => ({
                    group: header.name,
                    items: header.categories.map(category => ({
                        value: category.id.toString(),
                        label: category.name
                    }))
                }))
                : [];

            console.log('Transaction category options length:', categoryOptions.length);

            return (
                <Stack>
                    <Select
                        label="Target Budget Category"
                        placeholder="Select budget category"
                        searchable
                        data={categoryOptions}
                        value={form.values.target_budget_category_id?.toString()}
                        onChange={(value) => {
                            if (value) {
                                form.setFieldValue('target_budget_category_id', parseInt(value, 10));
                            } else {
                                form.setFieldValue('target_budget_category_id', undefined);
                            }
                        }}
                        error={form.errors.target_budget_category_id}
                        disabled={submitting || loadingBudgetCategories}
                    />
                </Stack>
            );
        } else if (ruleType === 'group_expense') {
            // For group expense rules, we need to first select a group

            // Get the rule amount based on the rule field and value
            let ruleAmount = 0;

            // Look at conditions to find amount
            const amountCondition = form.values.conditions?.find(c => c.field === 'amount');

            if (amountCondition) {
                // If the rule is based on amount, use that value
                const amountValue = amountCondition.value || '0';
                // Handle both string and number types, and handle operators like 'between'
                if (amountCondition.operator === 'between' && typeof amountValue === 'string' && amountValue.includes(',')) {
                    // For 'between' operator, use the average of min and max
                    const [min, max] = amountValue.split(',').map(val => parseFloat(val) || 0);
                    ruleAmount = (min + max) / 2;
                } else {
                    ruleAmount = parseFloat(amountValue);
                }
            } else if (transaction && transaction.amount) {
                // If we have a transaction, use its amount
                ruleAmount = Math.abs(transaction.amount);
            }

            // Ensure amount is a valid number
            ruleAmount = isNaN(ruleAmount) ? 0 : ruleAmount;

            return (
                <Stack>
                    <Select
                        label="Group"
                        placeholder="Select group"
                        data={
                            userGroups && userGroups.length > 0
                                ? userGroups
                                    .filter(group => group && typeof group.id !== 'undefined' && group.id !== null && typeof group.name === 'string')
                                    .map(group => ({
                                        value: String(group.id),
                                        label: group.name,
                                    }))
                                : []
                        }
                        value={form.values.group_id?.toString()}
                        onChange={(value) => {
                            if (value) {
                                form.setFieldValue('group_id', parseInt(value, 10));
                            } else {
                                form.setFieldValue('group_id', undefined);
                            }
                        }}
                        error={form.errors.group_id}
                        disabled={submitting || loadingUserGroups}
                    />

                    <Text size="sm" fw={500} mt="md">
                        Split Configuration {ruleAmount > 0 && `($${ruleAmount.toFixed(2)})`}
                    </Text>
                    {groupMembers.length > 0 ? (
                        <RuleSplitControls
                            form={form}
                            members={groupMembers}
                            amount={ruleAmount}
                        />
                    ) : (
                        <Text size="sm" c="dimmed">
                            {loadingGroupMembers ? 'Loading group members...' : 'No group members found'}
                        </Text>
                    )}
                </Stack>
            );
        }

        return null;
    };

    // Log budgetCategories in a useEffect
    useEffect(() => {
        console.log('RuleModal - Budget Categories state:', {
            categoriesCount: budgetCategories?.length || 0,
            headersCount: budgetHeaders?.length || 0,
            loading: loadingBudgetCategories,
            error,
            firstFewCategories: budgetCategories?.slice(0, 3) || []
        });
    }, [budgetCategories, budgetHeaders, loadingBudgetCategories]);

    return (
        <Modal
            opened={opened}
            onClose={resetAndClose}
            title={initialRule ? 'Edit Rule' : 'Create New Rule'}
            size="lg"
        >
            <LoadingOverlay visible={submitting} />

            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stepper active={activeStep} onStepClick={setActiveStep}>
                    {steps.map((step, index) => (
                        <Stepper.Step
                            key={index}
                            label={step.label}
                            description={step.description}
                            completedIcon={
                                <ThemeIcon
                                    size={24}
                                    color="green"
                                    radius="xl"
                                >
                                    <img src={CheckIcon} alt="Check" width={14} height={14} />
                                </ThemeIcon>
                            }
                        >
                            {index === 0 && (
                                <>
                                    <TextInput
                                        label="Rule Name"
                                        placeholder="Enter rule name"
                                        required
                                        {...form.getInputProps('name')}
                                        mb="md"
                                    />

                                    <Select
                                        label="Rule Type"
                                        placeholder="Select rule type"
                                        required
                                        data={RULE_TYPE_OPTIONS}
                                        value={ruleType}
                                        onChange={handleRuleTypeChange}
                                        mb="md"
                                    />

                                    <RuleConditionsControls
                                        conditions={form.values.conditions || []}
                                        onChange={(conditions) => form.setFieldValue('conditions', conditions)}
                                        getOperatorsForField={(field) => getOperatorsForField(field as any)}
                                        accounts={accounts}
                                        transactionForPrefill={transaction}
                                        budgetCategories={budgetCategories}
                                        budgetHeaders={budgetHeaders}
                                    />

                                    <Group justify="right" mt="md">
                                        <Button onClick={() => setActiveStep(1)}>
                                            Next
                                        </Button>
                                    </Group>
                                </>
                            )}

                            {index === 1 && (
                                <Stack mt="md">
                                    {renderCategorySelector()}

                                    {/* Only show the Enable Rule switch for edit mode */}
                                    {initialRule && (
                                        <Switch
                                            label="Enable Rule"
                                            checked={form.values.enabled}
                                            onChange={(event) => form.setFieldValue('enabled', event.currentTarget.checked)}
                                            mt="md"
                                        />
                                    )}

                                    {/* Add the Apply to past transactions toggle for new rules */}
                                    {!initialRule && (
                                        <Switch
                                            label="Apply to past transactions"
                                            description="Retroactively apply this rule to transactions from the past 90 days"
                                            checked={form.values.apply_to_past_transactions}
                                            onChange={(event) => form.setFieldValue('apply_to_past_transactions', event.currentTarget.checked)}
                                            mt="md"
                                        />
                                    )}

                                    <NumberInput
                                        label="Priority"
                                        description="Higher values have higher priority when multiple rules match"
                                        min={0}
                                        max={100}
                                        {...form.getInputProps('priority')}
                                    />

                                    <Group justify="right" mt="md">
                                        <Button onClick={() => setActiveStep(0)} variant="subtle">
                                            Back
                                        </Button>
                                        {initialRule && (
                                            <Button
                                                type="button"
                                                color="red"
                                                variant="subtle"
                                                onClick={handleDelete}
                                            >
                                                Delete Rule
                                            </Button>
                                        )}
                                        <Button type="submit" loading={submitting}>
                                            {initialRule ? 'Update Rule' : 'Create Rule'}
                                        </Button>
                                        {/* Apply Once button – only for new rules tied to a transaction */}
                                        {!initialRule && transaction && (
                                            <Button variant="outline" onClick={handleApplyOnce} loading={submitting}>
                                                Apply Once
                                            </Button>
                                        )}
                                    </Group>
                                </Stack>
                            )}
                        </Stepper.Step>
                    ))}
                </Stepper>
            </form>
        </Modal>
    );
}; 